import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { asyncScheduler } from 'rxjs';
import { catchError, filter, map, switchMap, throttleTime } from 'rxjs/operators';
import { openNotificationsDrawer, userAuthenticated } from '@capital-access/common/actions';
import { CommonLazyLoadingService } from '@capital-access/common/lazy-loading';
import {
  buildGroupedNotificationListRequest,
  NOTIFICATION_TYPE_CONFIGS,
  NotificationsDrawerActions,
  NotificationsDrawerModule,
  NotificationsHubService
} from '@capital-access/notifications/drawer';
import {
  LOAD_NOTIFICATIONS_DRAWER_MODULE,
  NOTIFICATIONS_DRAWER_MODULE_ID
} from '../notifications-drawer-module-id.model';

@Injectable()
export class NotificationsDrawerLazyEffects {
  openNotificationsDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openNotificationsDrawer),
        switchMap(() =>
          this.lazyLoadingService
            .loadModule(NOTIFICATIONS_DRAWER_MODULE_ID, LOAD_NOTIFICATIONS_DRAWER_MODULE)
            .pipe(switchMap(moduleRef => [(moduleRef.instance as NotificationsDrawerModule).openDrawer()]))
        )
      ),
    { dispatch: false }
  );

  onUserAuthenticated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthenticated),
      map(() => NotificationsDrawerActions.loadNotificationCount())
    )
  );

  routeChangeCount$ = createEffect(() =>
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      throttleTime(30_000, asyncScheduler, { leading: true, trailing: false }),
      map(() => NotificationsDrawerActions.loadNotificationCount())
    )
  );

  loadNotificationCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.loadNotificationCount),
      switchMap(() => {
        const defaultLimits: Record<string, number> = {};
        for (const [code, config] of Object.entries(NOTIFICATION_TYPE_CONFIGS)) {
          defaultLimits[code] = config.initialLimit;
        }
        return this.notificationsService.getNotificationCounts(buildGroupedNotificationListRequest(defaultLimits)).pipe(
          map(response =>
            NotificationsDrawerActions.loadNotificationCountSuccess({ unreadCount: response.unreadCount })
          ),
          catchError(error => [
            NotificationsDrawerActions.loadNotificationCountFailure({
              error: error.message ?? 'Failed to load notification count'
            })
          ])
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private lazyLoadingService: CommonLazyLoadingService,
    private notificationsService: NotificationsHubService,
    private router: Router
  ) {}
}
